import React, { useEffect, useState, useCallback } from 'react';
import { useAppShell } from '../app-shell';
import { Link } from 'react-router-dom';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Paper,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useInView } from 'react-intersection-observer';

interface Community {
  recordID: string;
  name?: string;
  description?: string;
  privacy?: string;
}

type ChipColor = 'success' | 'warning' | 'info' | 'default';

const getPrivacyColor = (privacy?: string): ChipColor => {
  switch (privacy) {
    case 'open':
      return 'success';
    case 'inviteOnly':
      return 'warning';
    case 'adminApprovalRequired':
      return 'info';
    default:
      return 'default';
  }
};

const getPrivacyLabel = (privacy?: string): string => {
  switch (privacy) {
    case 'open':
      return 'Open';
    case 'inviteOnly':
      return 'Invite Only';
    case 'adminApprovalRequired':
      return 'Approval Required';
    default:
      return privacy ?? '';
  }
};

const CommunityListPage = (): React.JSX.Element => {
  const { setBreadcrumb } = useAppShell();
  useEffect(() => {
    setBreadcrumb(['Communities']);
    return () => setBreadcrumb([]);
  }, [setBreadcrumb]);

  const [communities, setCommunities] = useState<Community[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);

  const { ref, inView } = useInView({
    threshold: 0,
    triggerOnce: false,
  });

  const fetchCommunities = useCallback(
    async (isLoadMore = false): Promise<void> => {
      if (loading) return;

      setLoading(true);
      setError(null);

      try {
        const currentOffset = isLoadMore ? offset : 0;
        const response = await fetch(`/api/communities?limit=50&offset=${currentOffset}`);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = (await response.json()) as { data?: Community[] };
        const newCommunities = data.data ?? [];

        if (isLoadMore) {
          setCommunities((prev) => [...prev, ...newCommunities]);
        } else {
          setCommunities(newCommunities);
        }

        setHasMore(newCommunities.length === 50);

        if (isLoadMore && newCommunities.length > 0) {
          setOffset((prev) => prev + newCommunities.length);
        } else if (!isLoadMore) {
          setOffset(newCommunities.length);
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        setError(message);
      } finally {
        setLoading(false);
      }
    },
    [offset, loading],
  );

  useEffect(() => {
    void fetchCommunities(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (inView && hasMore && !loading) {
      void fetchCommunities(true);
    }
  }, [inView, hasMore, loading, fetchCommunities]);

  const renderCommunityItem = (community: Community): React.JSX.Element => {
    const isOpen = community.privacy === 'open';
    const communityUrl = `/app/communities/${community.recordID}`;

    const content = (
      <ListItem
        key={community.recordID}
        sx={{
          mb: 1,
          borderRadius: 1,
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }}
      >
        <ListItemText
          primary={
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="h6" component="div">
                {community.name}
              </Typography>
              <Chip
                label={getPrivacyLabel(community.privacy)}
                color={getPrivacyColor(community.privacy)}
                size="small"
                variant="outlined"
              />
            </Box>
          }
          secondary={
            <Typography variant="body2" color="text.secondary">
              {community.description}
            </Typography>
          }
        />
      </ListItem>
    );

    if (isOpen) {
      return (
        <Link
          key={community.recordID}
          to={communityUrl}
          style={{ textDecoration: 'none', color: 'inherit' }}
        >
          {content}
        </Link>
      );
    }

    return content;
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 2 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Communities
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Error loading communities: {error}
        </Alert>
      )}

      <Paper elevation={2}>
        <List>
          {communities.map(renderCommunityItem)}

          {hasMore && (
            <div ref={ref}>
              <Box display="flex" justifyContent="center" p={2}>
                {loading ? (
                  <CircularProgress size={24} />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Loading more communities...
                  </Typography>
                )}
              </Box>
            </div>
          )}
        </List>

        {!loading && communities.length === 0 && (
          <Box p={4} textAlign="center">
            <Typography variant="h6" color="text.secondary">
              No communities found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Be the first to create a community!
            </Typography>
          </Box>
        )}
      </Paper>

      {!hasMore && communities.length > 0 && (
        <Box textAlign="center" p={2}>
          <Typography variant="body2" color="text.secondary">
            no more communities to load
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default CommunityListPage;
